import { getApiClient } from "@src/api";
import {
	invalidateNavbarBranchSnapshots,
	optimisticallyRemoveNavbarItem,
	restoreNavbarBranchSnapshots,
} from "@src/components/board/mutations/navbar-optimistic";
import { navbarBranchQueryKeys } from "@src/components/board/queries/fetch-ranklist";
import { parseCardDocumentId } from "@src/components/collaboration/document-id/factories";
import { libraryQueryKeys } from "@src/components/library/queries/query-keys";
import { graphql } from "@src/gql/gql";
import { handleError } from "@src/utils/error-handling";
import { showSuccessNotification } from "@src/utils/notifications";
import { useMutation, useQueryClient } from "@tanstack/react-query";

const leaveCardMutation = graphql(/* GraphQL */ `
	mutation leaveCard($cardId: String!) {
		cards {
			leave(cardId: $cardId) {
				id
				commandId
				correlationId
				causationId
			}
		}
	}
`);

/**
 * Leave a shared card (Shared With Me). GraphQL: `cards.leave`.
 */
export function useLeaveCard(showNotification = true) {
	const client = getApiClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (cardId: string) => {
			const result = await client.mutate({
				mutation: leaveCardMutation,
				variables: {
					cardId: parseCardDocumentId(cardId).toIdString(),
				},
			});

			if (result.errors && result.errors.length > 0) {
				throw new Error(result.errors[0]?.message ?? "Failed to leave card");
			}

			return result.data?.cards?.leave;
		},
		onMutate: async (cardId) => {
			const snapshots = await optimisticallyRemoveNavbarItem(
				queryClient,
				cardId,
			);
			return { snapshots };
		},
		onSuccess: (_, cardId) => {
			if (showNotification) {
				showSuccessNotification("Success", "Left successfully");
			}
			const typedCardId = parseCardDocumentId(cardId).toString();
			void queryClient.invalidateQueries({
				queryKey: libraryQueryKeys.myCard.byId(typedCardId),
			});
			void queryClient.invalidateQueries({
				queryKey: libraryQueryKeys.entities.all,
			});
			void queryClient.invalidateQueries({
				queryKey: libraryQueryKeys.entityTypeCounts.all,
			});
		},
		onError: (error, _vars, context) => {
			if (context?.snapshots) {
				restoreNavbarBranchSnapshots(queryClient, context.snapshots);
			}
			handleError(error, "Failed to leave card");
		},
		onSettled: (_data, _error, _vars, context) => {
			invalidateNavbarBranchSnapshots(queryClient, context?.snapshots ?? []);
			void queryClient.invalidateQueries({
				queryKey: navbarBranchQueryKeys.all(),
			});
		},
	});
}
